import React, { useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Spin, Tooltip, message } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayout from '../../../../components/Layout/StandardLayout';
import AudienceBuilder from '../../../shared/AudienceBuilder';
import AudiencePreviewModal from '../../../shared/AudienceBuilder/AudiencePreviewModal';
import MassMessageForm from '../../../shared/MassMessageForm';
import { MASS_MESSAGE_TEMPLATE_BUTTONS } from '../../../shared/MassMessageForm/constants';
import { useAudienceCount } from '../../../../hooks/query/useAudiences';
import { useCreateMassMessage } from '../../../../hooks/query/useMassMessages';
import { getMassMessageErrorMessage } from '../../../../services/massMessages';
import './styles.scss';

const NewMassMessage2 = () => {
  const history = useHistory();
  const formRef = useRef();
  const createMutation = useCreateMassMessage();
  const audienceCountMutation = useAudienceCount();

  // Audience
  const [audienceConfig, setAudienceConfig] = useState({ version: 1, operator: 'AND', tags: { included: [] } });
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const audienceCount = audienceCountMutation.data?.total ?? audienceCountMutation.data?.count ?? 0;
  const isCountLoading = audienceCountMutation.isLoading;

  const handleAudienceChange = useCallback(
    newConfig => {
      setAudienceConfig(newConfig);
      const hasFilters =
        newConfig?.tags?.included?.length > 0 ||
        newConfig?.tags?.excluded?.length > 0 ||
        newConfig?.stores?.included?.length > 0 ||
        newConfig?.contactMethods?.length > 0 ||
        !!newConfig?.lastContacted;

      if (hasFilters) {
        audienceCountMutation.mutate(newConfig);
      } else {
        audienceCountMutation.reset();
      }
    },
    [audienceCountMutation],
  );

  const hasAudienceFilters = useCallback(
    () =>
      audienceConfig?.tags?.included?.length > 0 ||
      audienceConfig?.tags?.excluded?.length > 0 ||
      audienceConfig?.stores?.included?.length > 0 ||
      audienceConfig?.contactMethods?.length > 0 ||
      !!audienceConfig?.lastContacted,
    [audienceConfig],
  );

  const handleFormSubmit = async formData => {
    if (!hasAudienceFilters()) {
      message.error('Please add at least one audience filter');
      return;
    }

    const payload = {
      ...formData,
      audience: { configuration: audienceConfig },
    };

    try {
      await createMutation.mutateAsync(payload);
      message.success('Message scheduled successfully');
      history.push('/Messaging/mass-messaging');
    } catch (error) {
      console.error('Error creating mass message:', error);
      message.error(getMassMessageErrorMessage(error, 'Failed to schedule message. Please try again.'));
    }
  };

  const audienceSection = (
    <div className="new-mm__section">
      <h3 className="new-mm__section-title">
        <FontAwesomeIcon icon={['far', 'users']} style={{ marginRight: 8 }} />
        AUDIENCE
      </h3>
      <div className="new-mm__audience-row">
        <div className="new-mm__audience-builder">
          <AudienceBuilder
            configuration={audienceConfig}
            onChange={handleAudienceChange}
            audienceCount={audienceCount}
            isCountLoading={isCountLoading}
          />
        </div>
        <div className="new-mm__audience-info">
          <div className="new-mm__audience-count">
            {isCountLoading ? (
              <Spin size="small" />
            ) : (
              <span className="new-mm__audience-count-number">{audienceCount.toLocaleString()}</span>
            )}
            <span className="new-mm__audience-count-label">recipients</span>
          </div>
          {audienceCount > 0 && (
            audienceCount > 5000 ? (
              <Tooltip title="Preview is not available for audiences larger than 5,000 clients">
                <span className="new-mm__preview-link new-mm__preview-link--disabled">Preview Audience</span>
              </Tooltip>
            ) : (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <span className="new-mm__preview-link" onClick={() => setShowPreviewModal(true)}>
                Preview Audience
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <StandardLayout
      title="Scheduled message"
      backLink="/Messaging/mass-messaging"
      breadCrumbsAboveTitle={[
        { title: 'Messaging' },
        { title: 'Mass Messaging', to: '/Messaging/mass-messaging' },
        { title: 'New Message' },
      ]}
      actions={
        <Button type="primary" onClick={() => formRef.current?.submit()} loading={createMutation.isLoading}>
          Schedule to Send
        </Button>
      }
    >
      <MassMessageForm
        ref={formRef}
        audienceCount={audienceCount}
        audienceSection={audienceSection}
        templateButtons={MASS_MESSAGE_TEMPLATE_BUTTONS}
        enableAttachments
        onSubmit={handleFormSubmit}
        isSaving={createMutation.isLoading}
      />

      <AudiencePreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        configuration={audienceConfig}
      />
    </StandardLayout>
  );
};

export default NewMassMessage2;
